// Level 2 night colour at the door (sprint 33 item 3; owner decision 1: the door uses A1 `a1-neighbour-L2`).
// The one-bit frame (bits.js) is drawn exactly as before; this only decides what colour each of its pixels shows:
//   - the door's wood and the reeded glass round the visitor: the Aseprite pixel of client/art/aseprite/night-door/ (built by
//     art/aseprite/src/night_door.lua from door.lua's own level 2 door and glass), cut out by the pixel's `art` code;
//   - every other ink pixel is the level 2 night ink, every other paper pixel its cream (the UI paper of the L2 sources);
//   - the accents (gold, red, plague green, the knock lights) keep their own colours; the Knocker's dark red is L2's red2;
//   - a bull, bear or lit knock's glass and an outbreak's stay one bit (#000 / #fff), as in the approved A5 L2 files.
// Nothing is drawn here: every colour is either a palette entry or a pixel read from the exported sheet.
import { decodeIndexed } from './eyes.js';
import { PALETTE } from './bits.js';

// The level 2 palette, index for index as art/aseprite/src/palettes.lua PAL.L2 (index 0 is clear). test/night-door.test.js
// holds this list to the Lua source and to the sheet's own PLTE.
export const NIGHT_L2 = ['#0b0d1a', '#141b38', '#1e2b58', '#2c4386', '#4766b4', '#86a3dc', '#9a4e2e', '#e3923a', '#ffcc6e',
  '#f6ecd2', '#c9b691', '#6e1426', '#e24034', '#5cb8ff', '#2f6fbf'];
const L2 = Object.fromEntries(['ink', 'n1', 'n2', 'b3', 'b4', 'b5', 'a1', 'a2', 'a3', 'cream', 'cream2', 'red2', 'red', 'sig', 'sig2'].map((n, i) => [n, NIGHT_L2[i]]));
// ink, paper, the snow on the glass (A1's flakes), the Knocker's outer bloom, and a visitor's lit pixels seen through the glass
// (the lamp's rim and the reeds' glints: A1 lights its figure's edge; the claimed name's colour there waits for the cards, so the
// glass's own cool lit blue stands in)
export const NIGHT = { ink: L2.ink, paper: L2.cream, snow: L2.b5, dark: L2.red2, figure: L2.b4 };
// the colours the night door may put on screen besides the art bible's accents and knock lights: the L2 sheet's own
// (minus the two signature blues, which no door pixel uses: the claimed name's colour waits for the cards, sprint 34)
export const NIGHT_COLOURS = NIGHT_L2.filter(c => c !== L2.sig && c !== L2.sig2);

// the door leaf the sheet covers, and where the glass sits in it (scene-onebit.js: the door is at glass (-24, -16), 228 x 295)
export const LEAF = { w: 228, h: 295, gx: 24, gy: 16 };
// art codes (bits.js `art`): 1 .. w*h a leaf pixel; the rest are roles
const ROLE = 1 << 20;
// TRIM: the one-bit door's own lines (its outline, panels, knob and frame), shown as ink and paper here, hidden by the toy door (toy.js)
// GLASS: whatever the glass painter left uncoded (the visitor's eyes, a drawn head), so toy town can tell it from a plate drawn after
export const ART = { SNOW: ROLE + 1, DARK: ROLE + 2, ONEBIT: ROLE + 3, FIG: ROLE + 4, TRIM: ROLE + 5, GLASS: ROLE + 6 };
// the leaf pixel for glass pixel (u, v), or leaf pixel (x, y) of the door
export const glassArt = (u, v) => (v + LEAF.gy) * LEAF.w + u + LEAF.gx + 1;
export const leafArt = (x, y) => (x >= 0 && y >= 0 && x < LEAF.w && y < LEAF.h ? y * LEAF.w + x + 1 : 0);

// the sheet's frames: tag lamp (the porch lamp at these levels), then tag ground (the street, lamp 1)
export const LAMPS = [0.25, 0.45, 0.6, 0.75, 1, 1.2];
export const GROUND_FRAME = LAMPS.length;
export const plateFrame = (lamp = 1, ground = false) => {
  if (ground) return GROUND_FRAME;
  let best = 0;
  LAMPS.forEach((l, i) => { if (Math.abs(l - lamp) < Math.abs(LAMPS[best] - lamp)) best = i; });
  return best;
};

// { w, index } of the sheet + its JSON -> { frames: [Uint8Array(w*h) palette indices] }
export function parseNight({ w, index }, json) {
  const frames = json.frames.map(({ frame: r }) => {
    const a = new Uint8Array(LEAF.w * LEAF.h);
    for (let y = 0; y < LEAF.h; y++) for (let x = 0; x < LEAF.w; x++) a[y * LEAF.w + x] = index[(r.y + y) * w + r.x + x];
    return a;
  });
  return { frames };
}

const BASE = new URL('./art/aseprite/night-door/', import.meta.url);
const isNode = !!globalThis.process?.versions?.node;
async function bytes(url) {
  if (isNode) { const fs = 'node:fs/promises'; return new Uint8Array(await (await import(fs)).readFile(url)); }
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return new Uint8Array(await r.arrayBuffer());
}
export async function loadNight() {
  const png = await decodeIndexed(await bytes(new URL('night-door-sheet.png', BASE)));
  const json = JSON.parse(new TextDecoder().decode(await bytes(new URL('night-door-sheet.json', BASE))));
  return parseNight(png, json);
}

const abgr = c => (0xff000000 | (parseInt(c.slice(5, 7), 16) << 16) | (parseInt(c.slice(3, 5), 16) << 8) | parseInt(c.slice(1, 3), 16)) >>> 0;
const PAL32 = [0xff000000, ...NIGHT_L2.map(abgr)];
const TINT32 = PALETTE.map(abgr), INK32 = abgr(NIGHT.ink), PAPER32 = abgr(NIGHT.paper), SNOW32 = abgr(NIGHT.snow), DARK32 = abgr(NIGHT.dark), FIG32 = abgr(NIGHT.figure);
const ONE_INK = abgr('#000000');

// Write the frame B (bits.js) as level 2 night colour into d32. sheet: loadNight()'s; frame: plateFrame(); plates: false for a
// frame that shows no Aseprite plate (the wrong Open's invert flash: the frame is ink and paper, inverted).
export function blitNight(B, d32, sheet, frame, { plates = true } = {}) {
  const { bits, tint, art } = B, n = B.w * B.h, P = plates && sheet?.frames[frame], N = LEAF.w * LEAF.h;
  for (let i = 0; i < n; i++) {
    const a = art[i], on = bits[i];
    if (a) {
      if (a <= N) { if (P) { const k = P[a - 1]; if (k) { d32[i] = PAL32[k]; continue; } } }
      else if (a === ART.ONEBIT) { d32[i] = on ? TINT32[tint[i]] : ONE_INK; continue; }
      else if (a === ART.SNOW && P) { d32[i] = SNOW32; continue; }
      else if (a === ART.DARK && P && !on) { d32[i] = DARK32; continue; }
      else if (a === ART.FIG && P && on && !tint[i]) { d32[i] = FIG32; continue; }
    }
    d32[i] = on ? (tint[i] ? TINT32[tint[i]] : PAPER32) : INK32;
  }
}

// The sheet the game uses. Missing (offline, a 404): the door stays one bit, never a door redrawn in code.
let sheet = null;
try { sheet = await loadNight(); } catch (e) { console.warn('night door:', e.message); }
export const nightSheet = () => sheet;
export const setNightSheet = s => { sheet = s; };
